import React, { useEffect, useRef, useState } from 'react';
import { FileText, MessageSquare, Image, LayoutGrid } from 'lucide-react';
import { PAQSHOT_APP_URL } from '@/config/hosts';

interface AppsMenuProps {
  openDirection?: 'up' | 'down';
  variant?: 'default' | 'sidebar';
}

export default function AppsMenu({ openDirection = 'down', variant = 'default' }: AppsMenuProps) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const apps = [
    {
      name: 'ProseCaptain Writer',
      url: 'https://app.prosecaptain.com/articles',
      icon: FileText
    },
    {
      name: 'ProseCaptain Chat',
      url: 'https://app.prosecaptain.com/widgets',
      icon: MessageSquare
    },
    {
      name: 'Paqshot Studio',
      url: PAQSHOT_APP_URL,
      icon: Image
    }
  ];

  const toggleMenu = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && rootRef.current) {
        const rect = rootRef.current.getBoundingClientRect();
        // Position dropdown just below the button, clamped to viewport on the right
        const width = 256; // w-64
        const padding = 8;
        const maxLeft = window.innerWidth - width - padding;
        const left = Math.min(rect.left, Math.max(0, maxLeft));
        const top = openDirection === 'up' ? rect.top - 8 : rect.bottom + 8;
        setMenuPosition({ top, left });
      }
      return next;
    });
  };

  return (
    <div ref={rootRef} className="relative z-[300]">
      <button
        onClick={toggleMenu}
        className={
          variant === 'sidebar'
            ? 'w-10 h-10 rounded-md flex items-center justify-center transition-all text-text-secondary hover:text-text-primary hover:bg-bg-control'
            : 'h-8 w-8 rounded-full bg-bg-control border border-border hover:bg-bg-control-hover transition-all flex items-center justify-center'
        }
        title="Paqshot Apps"
      >
        <LayoutGrid
          className={variant === 'sidebar' ? 'w-5 h-5' : 'w-4 h-4 text-text-primary'}
          strokeWidth={variant === 'sidebar' ? 1.8 : 2}
        />
      </button>

      {open && menuPosition && (
        <div
          className="fixed z-[99999999999] w-64 bg-bg-panel border border-border rounded-xl shadow-2xl overflow-hidden p-2"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            transform: openDirection === 'up' ? 'translateY(-100%)' : undefined
          }}
        >
          <div className="flex flex-col gap-1">
            {apps.map((app) => {
              const Icon = app.icon;
              return (
                <a
                  key={app.name}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-bg-control transition-all group"
                  onClick={() => setOpen(false)}
                >
                  <div className="w-8 h-8 rounded-full bg-bg-control group-hover:bg-bg-control-hover flex items-center justify-center transition-colors">
                    <Icon className="w-4 h-4 text-text-primary" strokeWidth={2} />
                  </div>
                  <span className="text-xs text-text-primary leading-tight">
                    {app.name}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

